import HeaderSection from '@/components/Header/HeaderSection.vue';
import BolideAssistant from "@/components/BolideAssistant/BolideAssistant.vue";
import { assistant_panel_mixin } from "@/components/BolideAssistant/assistant_panel_state";
import { get_user_profile, fetch_and_store_user_profile, get_token, set_user_profile } from '@/router';
import edit_profile_helpers from "@/components/EditProfile/edit_profile_helpers";

// profile urls
const view_user_url = process.env.VUE_APP_VIEW_USER_URL;
const user_management_url = process.env.VUE_APP_USER_MANAGEMENT_URL || process.env.VUE_APP_VIEW_USER_URL;
const update_user_url = process.env.VUE_APP_UPDATE_USER_URL;

export default {
    name: "EditProfile",

    mixins: [assistant_panel_mixin],

    components: {
        HeaderSection,
        BolideAssistant
    },

    data() {
        return ({
            breadcrumb: [
                { id: "home", title: "Dashboard" },
                { id: "profile", title: "Profile" },
                { id: "edit", title: "Edit Profile" }
            ],

            form: {
                first_name: "",
                last_name: "",
                email: "",
                phone: "",
                location: "Gauteng, South Africa",
                bio: ""
            },
            is_loading: false,
            is_saving: false,
            form_error: "",
            view_user_url: view_user_url,
            user_management_url: user_management_url,
            update_user_url: update_user_url
        });
    },

    mounted() {
        this.set_breadcrumb();
        this.loadUserProfile();
    },

    computed: {
        selected_user_id() {
            return this.parse_user_id(this.$route.query.user_id);
        },

        is_managed_user_edit() {
            return Boolean(this.selected_user_id);
        },

        page_title() {
            return this.is_managed_user_edit ? "Edit User" : "Edit Profile";
        },

        page_description() {
            return this.is_managed_user_edit
                ? "Update this user's personal details and contact information."
                : "Update your personal details and keep your contact information current.";
        },

        avatar_initials() {
            const first = this.form.first_name?.charAt(0) || "";
            const last = this.form.last_name?.charAt(0) || "";
            return (first + last).toUpperCase() || "UR";
        }
    },

    methods: {
        ...edit_profile_helpers,

        handleBreadcrumbClick(id) {
            if (id === "home") {
                this.$router.push("/home");
            }
            if (id === "profile") {
                this.$router.push("/profile");
            }
            if (id === "user-management") {
                this.$router.push("/user-management");
            }
        },

        set_breadcrumb() {
            this.breadcrumb = this.is_managed_user_edit
                ? [
                    { id: "home", title: "Dashboard" },
                    { id: "user-management", title: "User Management" },
                    { id: "edit", title: "Edit User" }
                ]
                : [
                    { id: "home", title: "Dashboard" },
                    { id: "profile", title: "Profile" },
                    { id: "edit", title: "Edit Profile" }
                ];
        },

        async resolve_profile_for_edit() {
            const profile = get_user_profile();

            if (this.is_managed_user_edit) {
                return this.load_managed_user(this.selected_user_id);
            }

            return profile || fetch_and_store_user_profile();
        },

        async populate_profile_form(profile) {
            if (!profile) {
                throw new Error("Failed to load user profile");
            }

            const user_id = this.get_selected_profile_id(profile);
            const next_profile = user_id && get_token()
                ? await this.load_profile_phone(profile, user_id)
                : profile;

            this.set_profile_edit(next_profile);
        },

        handle_profile_load_error(error) {
            this.form_error = error.message || "Failed to load profile data. Please try again.";
            console.error("Profile loading error:", error);

            if (!this.is_managed_user_edit) {
                this.set_profile_edit(get_user_profile());
            }
        },

        async loadUserProfile() {
            this.is_loading = true;
            this.form_error = "";

            try {
                const profile = await this.resolve_profile_for_edit();
                await this.populate_profile_form(profile);
            } catch (error) {
                this.handle_profile_load_error(error);
            } finally {
                this.is_loading = false;
            }
        },

        cancelEdit() {
            this.$router.push(this.get_return_path());
        },

        validate_user_payload() {
            if (!this.form.first_name || !this.form.last_name) {
                return "First name and last name are required.";
            }

            return "";
        },

        get_update_context(profile) {
            const raw_user_id = this.is_managed_user_edit
                ? this.selected_user_id
                : this.get_selected_profile_id(profile);
            const safe_user_id = this.parse_user_id(raw_user_id);

            return ({
                auth_token: get_token(),
                profile,
                profile_update_url: this.get_endpoint_url(this.update_user_url, safe_user_id, "update"),
                safe_user_id,
                user_update_url: this.get_user_update_url(safe_user_id)
            });
        },

        sync_current_user_profile(profile) {
            if (this.is_managed_user_edit) return;

            set_user_profile({
                ...(profile || {}),
                first_name: this.form.first_name,
                last_name: this.form.last_name,
                email: this.form.email,
                phone_number: this.form.phone
            });
        },

        validate_update_request(context) {
            const validation_error = this.validate_user_payload();

            if (validation_error) return validation_error;
            return this.get_update_context_error(context);
        },

        async persist_user_update(context) {
            await this.save_user_details(context.user_update_url);
            await this.save_profile_details(context.profile_update_url);
            this.sync_current_user_profile(context.profile);
        },

        async update_user() {
            const context = this.get_update_context(get_user_profile());
            const request_error = this.validate_update_request(context);

            if (request_error) {
                this.form_error = request_error;
                return false;
            }

            this.is_saving = true;
            this.form_error = "";

            try {
                await this.persist_user_update(context);
                return true;
            } catch (error) {
                this.form_error = error.message || "Unable to save user details.";
                return false;
            } finally {
                this.is_saving = false;
            }
        },

        async saveProfile() {
            const did_save = await this.update_user();

            if (did_save) {
                this.$router.push(this.get_return_path());
            }
        }
    }
};
